// DE profile › Configuration › Job (AGENT-MENTION, review #13; 06 §Main screens; 05 §DE
// configuration): what the profile's "Edit" opens. The DE's standing orders, editable here, and
// both versions side by side: Saved (the company's override, or the template's own) and Deployed
// (the text inside the bundle the DE runs now). A saved change reaches the DE only with its next
// deploy (a running DE is never hot-patched), so the section says whether they differ. Saving
// writes the same override as Setup › Standing orders & models (`PUT …/template-override`, the
// role-to-model map left as it is); who may save is that route's rule (admins, owners, the DE's
// supervisor). A DE with nothing deployed or staged has no template to override yet: the
// section says so. Calls: `GET …/standing-orders` (the override's readers).
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  Notice,
  SettingsGroup,
  SettingsRow,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { SlidersHorizontalIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { useOperatorName } from "../operator-name";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "./de-status";

const ordersKey = (co: string, de: string) => ["company", co, "de", de, "standing-orders"] as const;

export function DeJobTab({
  api,
  company,
  deId,
  name,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  name: string;
}) {
  const orders = useQuery({
    queryKey: ordersKey(company.id, deId),
    queryFn: () =>
      api.client.digitalEmployees.standingOrders({ params: { co: company.id, id: deId } }),
    retry: false,
  });
  if (orders.isPending) return <Loading label="Loading the standing orders" rows={4} />;
  if (orders.isError)
    return orders.error instanceof ApiError &&
      (orders.error.status === 403 || orders.error.status === 404) ? (
      <Notice tone="neutral" data-slot="job-no-access">
        {name}'s standing orders are shown to company admins and its supervisor.
      </Notice>
    ) : (
      <LoadError what="The standing orders" error={orders.error} />
    );
  return (
    <JobForm
      key={`${orders.data.saved?.version ?? 0}:${orders.data.template_id ?? ""}`}
      api={api}
      company={company}
      deId={deId}
      name={name}
      data={orders.data}
    />
  );
}

type Orders = Awaited<ReturnType<Api["client"]["digitalEmployees"]["standingOrders"]>>;

function JobForm({
  api,
  company,
  deId,
  name,
  data,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  name: string;
  data: Orders;
}) {
  const operator = useOperatorName();
  const qc = useQueryClient();
  const ids = { saved: useId(), deployed: useId() };
  const [text, setText] = useState(data.next ?? "");
  const [savedNow, setSavedNow] = useState(false);
  const templateId = data.template_id;
  const dirty = text !== (data.next ?? "");
  const save = useMutation({
    mutationFn: () => {
      if (!templateId) throw new Error("no template to override");
      return api.client.digitalEmployees.putTemplateOverride({
        params: { co: company.id, id: deId },
        body: { template_id: templateId, standing_orders_text: text.trim() ? text : null },
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ordersKey(company.id, deId) });
      setSavedNow(true);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSavedNow(false);
    save.mutate();
  };
  const editable = data.can_edit && templateId !== null;
  return (
    <form
      onSubmit={submit}
      className="flex min-w-0 flex-col gap-4"
      data-slot="de-job"
      data-pending={data.pending ? "true" : "false"}
    >
      <SettingsGroup
        title="Standing orders"
        actions={
          <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
            {data.template_name ? (
              <span className="text-muted-foreground">{data.template_name}</span>
            ) : null}
            {!templateId ? null : data.pending ? (
              <Badge variant="warning" dot data-slot="orders-state">
                Saved · not deployed yet
              </Badge>
            ) : data.deployed ? (
              <Badge variant="success" dot data-slot="orders-state">
                Deployed
              </Badge>
            ) : null}
          </span>
        }
      >
        {!templateId ? (
          <SettingsRow label="Not deployed yet" wide>
            <p className="text-sm text-muted-foreground" data-slot="job-no-template">
              {name} has no template deployed or staged, so there are no standing orders to edit
              yet. They can be set once {operator} stages its first deploy.
            </p>
          </SettingsRow>
        ) : (
          <>
            <SettingsRow
              label="Saved"
              hint={
                data.saved
                  ? `The company's own, v${data.saved.version} · ${formatDateTime(data.saved.updated_at)}. Applies with the next deploy.`
                  : "The template's own. Edit to override it; applies with the next deploy."
              }
              htmlFor={ids.saved}
              wide
            >
              <Textarea
                id={ids.saved}
                rows={8}
                maxLength={20000}
                readOnly={!editable}
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-slot="orders-saved"
              />
            </SettingsRow>
            <SettingsRow
              label="Deployed"
              hint={
                data.deployed
                  ? `What ${name} runs now${data.deployed.deployed_at ? `, since ${formatDateTime(data.deployed.deployed_at)}` : ""}.`
                  : `${name} runs no deployed bundle yet.`
              }
              htmlFor={ids.deployed}
              wide
            >
              {data.deployed?.text != null ? (
                <Textarea
                  id={ids.deployed}
                  rows={8}
                  readOnly
                  value={data.deployed.text}
                  data-slot="orders-deployed"
                  className="bg-sunken text-muted-foreground"
                />
              ) : (
                <p className="text-sm text-muted-foreground" data-slot="orders-deployed">
                  {data.deployed
                    ? `Bundle ${data.deployed.bundle_sha.slice(0, 12)} is in storage; its text is not shown here.`
                    : "Nothing deployed yet."}
                </p>
              )}
            </SettingsRow>
          </>
        )}
      </SettingsGroup>
      {save.isError ? (
        <Notice tone="danger" role="alert" title="The standing orders were not saved">
          {problemText(save.error, "The standing orders could not be saved.")}
        </Notice>
      ) : savedNow ? (
        <Notice tone="success" role="status" data-slot="orders-saved-note">
          Saved. {name} gets them with its next deploy.
        </Notice>
      ) : null}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Link
          to="/c/$slug/setup/de-overrides"
          params={{ slug: company.slug }}
          search={keepFocus}
          className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
        >
          <SlidersHorizontalIcon aria-hidden="true" />
          Models and the full override
        </Link>
        {editable ? (
          <Button type="submit" size="sm" disabled={!dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save standing orders"}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
